import { toQueryString } from "@circle-react/helpers/urlHelpers/toQueryString";
import type { Params } from "@circle-react/helpers/urlHelpers/toQueryString";

interface ParamsProps {
  params?: Params;
}

interface TaskIdProps {
  params?: Params;
  taskId: string;
}

interface TemplateIdProps {
  params?: Params;
  templateId: string;
}

interface TemplateIdWithOverridesProps {
  overrides?: Params;
  params?: Params;
  templateId: string;
}

interface TemplateKeyProps {
  templateKey: string;
}

interface IdProps {
  id: string;
  params?: Params;
}

interface WorkflowIdProps {
  params?: Params;
  workflowId: string;
}

interface WorkflowIdIdProps {
  id: string;
  params?: Params;
  workflowId: string;
}

interface WorkflowIdRunIdProps {
  params?: Params;
  runId: string;
  workflowId: string;
}

interface PaywallIdIdProps {
  id: string;
  params?: Params;
  paywallId: string;
}

const path = (path: string, params: Params = {}) =>
  `${path}?${toQueryString(params)}`;

const getWorkflowsBasePath = (useInternalApi: boolean) =>
  useInternalApi ? "/internal_api/workflows" : "/workflows/api/v1";

const createWorkflowsPaths = (useInternalApi: boolean) => {
  const basePath = getWorkflowsBasePath(useInternalApi);
  const workflowsPath = `${basePath}/workflows`;
  const triggerFn = ({ workflowId, id, params = {} }: WorkflowIdIdProps) =>
    path(`${workflowsPath}/${workflowId}/triggers/${id}`, params);

  return {
    create: ({ params = {} }: ParamsProps) => path(workflowsPath, params),
    list: ({ params = {} }: ParamsProps) => path(workflowsPath, params),
    fetch: ({ id, params = {} }: IdProps) =>
      path(`${workflowsPath}/${id}`, params),
    update: ({ id, params = {} }: IdProps) =>
      path(`${workflowsPath}/${id}`, params),
    activate: ({ id, params = {} }: IdProps) =>
      path(`${workflowsPath}/${id}/activate`, params),
    inactivate: ({ id, params = {} }: IdProps) =>
      path(`${workflowsPath}/${id}/inactivate`, params),
    archive: ({ id, params = {} }: IdProps) =>
      path(`${workflowsPath}/${id}/archive`, params),
    duplicate: ({ id, params = {} }: IdProps) =>
      path(`${workflowsPath}/${id}/duplicate`, params),
    delete: ({ id, params = {} }: IdProps) =>
      path(`${workflowsPath}/${id}`, params),
    createTrigger: ({ workflowId, params = {} }: WorkflowIdProps) =>
      path(`${workflowsPath}/${workflowId}/triggers`, params),
    updateTrigger: triggerFn,
    deleteTrigger: triggerFn,
    createRun: ({ workflowId, params = {} }: WorkflowIdProps) =>
      path(`${workflowsPath}/${workflowId}/runs`, params),
    fetchRuns: ({ workflowId, params = {} }: WorkflowIdProps) =>
      path(`${workflowsPath}/${workflowId}/runs`, params),
    fetchRun: ({ workflowId, runId, params = {} }: WorkflowIdRunIdProps) =>
      path(`${workflowsPath}/${workflowId}/runs/${runId}`, params),
    cancelRun: ({ workflowId, runId, params = {} }: WorkflowIdRunIdProps) =>
      path(`${workflowsPath}/${workflowId}/runs/${runId}/cancel`, params),
    fetchTasksInRun: ({
      workflowId,
      runId,
      params = {},
    }: WorkflowIdRunIdProps) =>
      path(`${workflowsPath}/${workflowId}/runs/${runId}/tasks`, params),
    createAudience: ({ workflowId, params = {} }: WorkflowIdProps) =>
      path(`${workflowsPath}/${workflowId}/audiences`, params),
    updateAudience: ({ workflowId, id, params = {} }: WorkflowIdIdProps) =>
      path(`${workflowsPath}/${workflowId}/audiences/${id}`, params),
    createAction: ({ workflowId, params = {} }: WorkflowIdProps) =>
      path(`${workflowsPath}/${workflowId}/actions`, params),
    updateAction: ({ workflowId, id, params = {} }: WorkflowIdIdProps) =>
      path(`${workflowsPath}/${workflowId}/actions/${id}`, params),
    deleteAction: ({ workflowId, id, params = {} }: WorkflowIdIdProps) =>
      path(`${workflowsPath}/${workflowId}/actions/${id}`, params),
    upsertActions: ({ workflowId, params = {} }: WorkflowIdProps) =>
      path(`${workflowsPath}/${workflowId}/actions/upsert`, params),
    createOneTimeSchedule: ({ workflowId, params = {} }: WorkflowIdProps) =>
      path(`${workflowsPath}/${workflowId}/schedule/one_time`, params),
    updateOneTimeSchedule: ({
      workflowId,
      id,
      params = {},
    }: WorkflowIdIdProps) =>
      path(`${workflowsPath}/${workflowId}/schedule/one_time/${id}`, params),
    createRecurringSchedule: ({ workflowId, params = {} }: WorkflowIdProps) =>
      path(`${workflowsPath}/${workflowId}/schedule/recurring`, params),
    updateRecurringSchedule: ({
      workflowId,
      id,
      params = {},
    }: WorkflowIdIdProps) =>
      path(`${workflowsPath}/${workflowId}/schedule/recurring/${id}`, params),
    reschedule: ({ workflowId, id, params = {} }: WorkflowIdIdProps) =>
      path(`${workflowsPath}/${workflowId}/schedule/reschedule/${id}`, params),
    tasks: ({ params = {} }: ParamsProps) => path(`${basePath}/tasks`, params),
    taskDetail: ({ taskId, params = {} }: TaskIdProps) =>
      path(`${basePath}/tasks/${taskId}`, params),
    templates: ({ params = {} }: ParamsProps) =>
      path(`${basePath}/templates`, params),
    fetchTemplate: ({ templateId, params = {} }: TemplateIdProps) =>
      path(`${basePath}/templates/${templateId}`, params),
    createFromTemplate: ({
      templateId,
      params = {},
    }: TemplateIdWithOverridesProps) =>
      path(`${basePath}/templates/${templateId}/create_workflow`, params),
    createFromTemplateKey: ({ templateKey }: TemplateKeyProps) =>
      path(`${basePath}/templates/${templateKey}/create_workflow`),
    fetchCommunityCurrentUsageStats: ({ params = {} }: ParamsProps) =>
      path(`${basePath}/community_current_usage_stats`, params),
    fetchInterpolatedMessage: ({ params = {} }: ParamsProps) =>
      path(`${basePath}/preview_messages/interpolated_message`, params),
    fetchDynamicVariables: ({ params = {} }: ParamsProps) =>
      path(`${basePath}/dynamic_variables`, params),
    testWebhook: ({ params = {} }: ParamsProps) =>
      path(`${basePath}/webhook_tests`, params),
    events: {
      index: ({ params = {} }: ParamsProps) =>
        path("/internal_api/workflows/events", params),
      show: ({ id, params = {} }: IdProps) =>
        path(`/internal_api/workflows/events/${id}`, params),
    },
    posts: {
      show: ({ id, params = {} }: IdProps) =>
        path(`/internal_api/workflows/posts/${id}`, params),
    },
    recurringEventSettings: {
      show: ({ id, params = {} }: IdProps) =>
        path(`/internal_api/workflows/recurring_event_settings/${id}`, params),
    },
    paywallPrices: {
      show: ({ paywallId, id, params = {} }: PaywallIdIdProps) =>
        path(
          `/internal_api/workflows/paywalls/${paywallId}/prices/${id}`,
          params,
        ),
    },
    previewMessages: {
      previewAttributes: ({ params = {} }: ParamsProps) =>
        path(
          "/internal_api/workflows/preview_messages/preview_attributes",
          params,
        ),
    },
    contacts: {
      index: ({ params = {} }: ParamsProps) =>
        path("/internal_api/workflows/contacts", params),
    },
  } as const;
};

export const workflows = createWorkflowsPaths(false);

export { createWorkflowsPaths };
